import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const TIERS = ["starter", "growth", "enterprise"];
const STATUSES = ["active", "trialing", "canceled", "expired", "inactive"];

export default function AdminSubscriptions() {
  const [data, setData] = useState(null);
  const [edit, setEdit] = useState({});

  const load = useCallback(() => api.get("/admin/subscriptions").then((r) => setData(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const save = async (uid) => {
    const e = edit[uid];
    if (!e?.tier || !e?.status) { toast.error("Choose a plan and status"); return; }
    try {
      await api.post(`/admin/users/${uid}/subscription`, { tier: e.tier, status: e.status });
      toast.success("Subscription updated"); setEdit((s) => ({ ...s, [uid]: undefined })); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div data-testid="admin-subscriptions">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <div className="rounded-2xl border border-[#D4AF37]/25 bg-[#151310] p-5">
          <div className="text-[#71717A] text-xs uppercase tracking-wider">Monthly recurring</div>
          <div className="font-heading text-3xl fv-gold-text mt-1" data-testid="admin-mrr">${data?.mrr ?? "—"}</div>
        </div>
        {TIERS.map((t) => (
          <div key={t} className="rounded-2xl border border-white/8 bg-[#121214] p-5">
            <div className="text-[#71717A] text-xs uppercase tracking-wider capitalize">{t}</div>
            <div className="font-heading text-3xl text-white mt-1">{data?.distribution?.[t] ?? 0}</div>
          </div>
        ))}
      </div>

      <div className="rounded-2xl border border-white/8 bg-[#121214] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-subs-table">
            <thead><tr className="border-b border-white/8 text-left text-[#71717A]">
              <th className="px-5 py-3 font-normal">User</th><th className="px-5 py-3 font-normal">Current</th>
              <th className="px-5 py-3 font-normal">Override plan</th><th className="px-5 py-3 font-normal">Status</th><th className="px-5 py-3 font-normal"></th>
            </tr></thead>
            <tbody>
              {(data?.subscribers || []).map((s) => {
                const e = edit[s.user_id] || {};
                return (
                  <tr key={s.user_id} className="border-b border-white/5 last:border-0" data-testid="admin-sub-row">
                    <td className="px-5 py-3"><div className="text-white">{s.name || "—"}</div><div className="text-xs text-[#71717A]">{s.email}</div></td>
                    <td className="px-5 py-3"><span className="text-[#D4AF37] capitalize">{s.subscription?.tier || "—"}</span> <span className="text-xs text-[#71717A] capitalize">· {s.subscription?.status}</span></td>
                    <td className="px-5 py-3">
                      <Select value={e.tier} onValueChange={(v) => setEdit((st) => ({ ...st, [s.user_id]: { ...e, tier: v } }))}>
                        <SelectTrigger className="w-32 bg-[#0d0d0f] border-white/10 text-white h-9" data-testid={`admin-sub-tier-${s.user_id}`}><SelectValue placeholder="Plan" /></SelectTrigger>
                        <SelectContent className="bg-[#1C1C1F] border-white/10 text-white">{TIERS.map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent>
                      </Select>
                    </td>
                    <td className="px-5 py-3">
                      <Select value={e.status} onValueChange={(v) => setEdit((st) => ({ ...st, [s.user_id]: { ...e, status: v } }))}>
                        <SelectTrigger className="w-32 bg-[#0d0d0f] border-white/10 text-white h-9" data-testid={`admin-sub-status-${s.user_id}`}><SelectValue placeholder="Status" /></SelectTrigger>
                        <SelectContent className="bg-[#1C1C1F] border-white/10 text-white">{STATUSES.map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent>
                      </Select>
                    </td>
                    <td className="px-5 py-3"><Button size="sm" onClick={() => save(s.user_id)} data-testid={`admin-sub-save-${s.user_id}`} className="fv-gold-gradient text-[#0A0A0B] rounded-full">Apply</Button></td>
                  </tr>
                );
              })}
              {(data?.subscribers || []).length === 0 && <tr><td colSpan={5} className="px-5 py-8 text-center text-[#71717A]">No subscriptions yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
